/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import type { CreateEvalsJobsBody, EvalTimelineDisplayEntry} from '../../../types/api';

import { cn } from '../../../lib/utils';
import Icons from '../../../icons/icons';
import { useLanguage } from '../../../providers/LanguageProvider';
import { formatEvalLabel, formatNumberByLocale } from '../../../lib/utils';
import { formatDateMonthDayYearTime } from '../../../utils/isoToStringDateFormat';
import { labelColors, sizeStyles } from '../../../utils/constants';

import BasicLoader from '../../common/basic-loader';
import { Button } from '../../common/button';


interface EvalListItemProps {
    latestEval:  EvalTimelineDisplayEntry;
    index: number;
    arr: EvalTimelineDisplayEntry[];
    size: "default" | "compact",
    templateName: string | undefined;
    sendExplanationRequest?: (body: CreateEvalsJobsBody) => void | Promise<void>;
    isExplaining?: boolean;
}

const EvalListItem = ({ latestEval, index, arr, size, templateName, sendExplanationRequest, isExplaining }: EvalListItemProps) => {
    const { messages } = useLanguage();
    const evaluationLang = (messages as any)?.Evaluation;

    const label = latestEval?.eval_result?.label;

    // Extract total tokens and check if they exist
    const totalTokens = latestEval?.eval_result?.total_tokens;
    const hasTokens = totalTokens != null && totalTokens !== '';

    // Extract user email and determine who corrected the evaluation
    const userEmail = latestEval?.user_email ?? latestEval?.eval_result?.user_email;
    const correctedBy = userEmail || 'user';

    // Determine if the evaluation is authoritative (manual)
    const isAuthoritative = latestEval?.category === 'manual';
    const userAction = latestEval?.userAction;
    const actionType = userAction === 'approve' ? evaluationLang?.approved_by : evaluationLang?.corrected_by;

    // Determine the color class for the evaluation label
    const colorClass = (latestEval?.eval_result?.label && labelColors[latestEval.eval_result.label as keyof typeof labelColors]) ?? labelColors.neutral;
    
    // Determine if this is the last item in the list
    const isLastItem = index === arr.length - 1;
    const itemKey = latestEval?.job_id || `${latestEval?.eval_timestamp}_${latestEval?.eval_result?.label}_${index}`;

    // Determine if the "Explain" button should be shown for this evaluation
    const showExplainBtn = latestEval?.isLatestResult && latestEval?.eval_result?.judge_source === 'decision_model'; 

    /**
     * Requests an explanation for the current evaluation verdict.
     */
    const requestExplanation = () => {
        const rawProbabilities: unknown = latestEval?.eval_result?.decision_model_probabilities;
        const confidence = latestEval?.eval_result?.decision_model_confidence;

        if(!label || !templateName || !rawProbabilities || confidence == null) 
            return;

        let probabilities: Record<string, number>;

        try {
            probabilities = typeof rawProbabilities === 'string'
                ? JSON.parse(rawProbabilities)
                : (rawProbabilities as Record<string, number>);

            const body =  {
                template_name: templateName,
                pinned_jev_result: {
                    label,
                    probabilities: probabilities,
                    confidence: Number(confidence)
                }
            };

            sendExplanationRequest?.(body);
        } catch {
            return;
        }
    };

  return (
    <li key={itemKey} className="relative flex gap-2 mb-4">             
        {arr?.length > 1 ? (
            index === 0 ? (
                Icons.CheckCircleOutline('mt-1.5 -ml-0.5 w-4 h-4 shrink-0')
            ) : (
                <div className="mt-2.5 w-2.5 h-2.5 shrink-0 rounded-full border border-[#40565A]"></div>
            )
        ) : null}
        
        {!isLastItem ? (
        <div className="absolute left-[5px] top-[20px] bottom-[-26px] w-px bg-[#40565A]"></div>
        ) : null}
        
        <div>
            <header className="flex items-center gap-2">
                <span className={cn(
                        'flex gap-2 items-center justify-center capitalize rounded-full border whitespace-nowrap',
                        sizeStyles[size],
                        colorClass
                    )}>
                    {userAction === 'approve' && Icons.UserApprovedIcon('size-4.5')}
                    {userAction === 'edit' && Icons.UserFeedbackIcon('size-4.5')}
                    {userAction === 'create' && Icons.UserAddedIcon('size-4.5')}
                    {formatEvalLabel(label || '')}
                </span>
                <time className="text-xs text-[#8C9EA1]">{formatDateMonthDayYearTime(latestEval?.eval_timestamp)}</time>
                {isAuthoritative ? (
                    <>
                        <span className="text-2xl text-[#40565A]">&middot;</span>
                        <span className="text-xs text-[#8C9EA1]">
                            {actionType} {correctedBy}
                        </span>
                    </>
                ) : hasTokens ? (
                    <>
                        <span className="text-2xl text-[#40565A]">&middot;</span>
                        <span className="text-xs text-[#8C9EA1]">
                            {formatNumberByLocale(Number(totalTokens))} {evaluationLang?.tokens}
                        </span>
                    </>
                ) : null}
            </header>
            <p className="text-[11px] text-[#8C9EA1] mt-2 leading-4">{latestEval?.eval_result?.explanation}</p> 

            {showExplainBtn && (
                isExplaining ? (
                    <div className="flex items-center gap-1.5 mt-3">
                        <BasicLoader className="size-4" />
                        <span className="text-xs text-[#8C9EA1]">{evaluationLang?.fetching_explanation}..</span>
                    </div>
                ) : (
                    <Button
                        variant="outline"
                        size="xs"
                        className="mt-3 gap-1 text-white py-2 hover:bg-transparent hover:text-white"
                        onClick={requestExplanation}
                    >
                        {Icons.ClipBoardIcon('w-4 h-4')} {evaluationLang?.explain}
                    </Button>
                )
            )}            
        </div>   
    </li>
  );
};

export default EvalListItem;